import { getCookie } from "@insite/client-framework/Common/Cookies";
import { createHandlerChainRunner, Handler } from "@insite/client-framework/HandlerCreator";
import logger from "@insite/client-framework/Logger";
import { PaginationModel } from "@insite/client-framework/Types/ApiModels";
import {
    ContentSearchCollectionModel,
    getContentSearch,
    GetContentSearchApiParameter,
} from "@solveindustrial/Services/ContentSearchService";
import { ContentFilters } from "@solveindustrial/Store/State/ContentSearch/ContentSearchState";
import qs from "qs";

interface Parameter {
    queryString?: string;
    page?: number;
    pageSize?: number;
}

export interface ContentSearchResults {
    contentSearch: ContentSearchCollectionModel;
    contentFilters: ContentFilters;
    pagination: PaginationModel;
    totalItemCount: number;
    isLoading: boolean;
}

interface Props {
    apiParameter: GetContentSearchApiParameter;
    result: ContentSearchResults;
}

type HandlerType = Handler<Parameter, Props>;

export const ParseQueryParameter: HandlerType = props => {
    const { queryString, page } = props.parameter;
    let { pageSize } = props.parameter;
    const parsedQuery = qs.parse(queryString?.startsWith("?") ? queryString.substr(1) : queryString || "");
    const { query } = parsedQuery;

    if (!pageSize) {
        const cookieValue = getCookie("ProductList-PageSize");
        if (cookieValue) {
            pageSize = parseInt(cookieValue, 10);
        }
    }

    props.result = props.result || {
        contentSearch: {} as ContentSearchCollectionModel,
        contentFilters: {} as ContentFilters,
        pagination: {} as PaginationModel,
        totalItemCount: 0,
        isLoading: false,
    };

    props.result.contentFilters = {
        query: typeof query === "string" ? query : queryString || "",
        page: page ? Number(page) : undefined,
        pageSize: pageSize ? Number(pageSize) : undefined,
    };
};

export const PopulateApiParameter: HandlerType = props => {
    const filters = props.result.contentFilters;
    const pagination = props.result.pagination;

    const { query, page, pageSize } = filters;

    props.apiParameter = {
        ...props.apiParameter,
        query: query ?? "",
        page: page ?? pagination.page,
        pageSize: pageSize ?? pagination.defaultPageSize,
    };

    props.dispatch({
        type: "ContentSearch/SetLoading",
        isLoading: true,
    } as any);

    if (props.parameter.queryString !== undefined) {
        props.dispatch({
            type: "ContentSearch/SetSearchQuery",
            searchQuery: props.parameter.queryString,
        } as any);
    }
};

export const RequestDataFromApi: HandlerType = async props => {
    try {
        props.result.contentSearch = await getContentSearch(props.apiParameter);

        props.dispatch({
            type: "ContentSearch/SetLoading",
            isLoading: false,
        } as any);
    } catch (error) {
        logger.warn(`Failed to execute content search: ${error}`);
        props.dispatch({
            type: "ContentSearch/SetError",
            error: "An error occurred while searching. Please try again.",
        } as any);
        throw error;
    }
};

export const ProcessApiResult: HandlerType = props => {
    const { pages, pagination } = props.result.contentSearch;

    props.dispatch({
        type: "ContentSearch/SetSearchResults",
        searchResults: pages,
        pagination,
    } as any);

    props.dispatch({
        type: "ContentSearch/SetContentFilters",
        contentFilters: props.result.contentFilters,
    } as any);
};

export const chain = [ParseQueryParameter, PopulateApiParameter, RequestDataFromApi, ProcessApiResult];

const executeContentSearch = createHandlerChainRunner(chain, "ExecuteContentSearch");
export default executeContentSearch;
